/**
 * Bascule entre comparatif de code et aperçu visuel (§3.5).
 *
 * Les deux vues répondent à deux questions différentes, et aucune ne remplace
 * l'autre : « qu'est-ce qui a changé dans le fichier ? » et « est-ce que mon
 * application a changé d'allure ? ». La seconde est celle que se posent les
 * auteurs qui ne lisent pas le code, c'est-à-dire la plupart.
 *
 * Extrait dans son propre composant parce que deux écrans le portent — le
 * comparatif de versions et le formulaire de mise à jour — et qu'une seconde
 * copie aurait fini par diverger de la première.
 */

import { Code2, Eye } from 'lucide-react';

import { Button } from './ui';

/** Vue active du comparatif. */
export type ComparisonView = 'code' | 'visual';

export interface ViewSwitchProps {
  value: ComparisonView;
  onChange: (view: ComparisonView) => void;
}

/** Deux boutons exclusifs, l'état actif porté par `aria-pressed`. */
export function ViewSwitch({ value, onChange }: ViewSwitchProps) {
  return (
    <div className="flex flex-wrap items-center gap-2">
      <span className="text-xs font-medium uppercase tracking-wide text-slate-500 dark:text-slate-400">
        Comparer
      </span>
      <Button
        variant={value === 'visual' ? 'primary' : 'secondary'}
        aria-pressed={value === 'visual'}
        onClick={() => onChange('visual')}
      >
        <Eye className="h-4 w-4" aria-hidden="true" />
        Visuel
      </Button>
      <Button
        variant={value === 'code' ? 'primary' : 'secondary'}
        aria-pressed={value === 'code'}
        onClick={() => onChange('code')}
      >
        <Code2 className="h-4 w-4" aria-hidden="true" />
        Code
      </Button>
      <span className="text-xs text-slate-500 dark:text-slate-400">
        {value === 'visual'
          ? 'Les deux versions rendues côte à côte, comme dans un navigateur.'
          : 'Les différences ligne à ligne.'}
      </span>
    </div>
  );
}
